import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycPartyCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { PartyDto } from "@/features/kyc/api/dto";
import type { SanctionsScreeningStatus } from "@/features/shared/api/enums";

/**
 * Enregistre le résultat du criblage sanctions/PEP
 * (`POST /Parties/{id}/sanctions-screening`, paramètres en query string).
 *
 * Un dossier KYC ne peut être approuvé que si le statut est CLEAR. Action
 * protégée maker-checker (guide §5.4) : le forçage de statut exige une
 * demande KYC_FORCE_OVERRIDE APPROVED — ce hook est l'EXÉCUTEUR appelé par
 * la page détail d'une ApprovalRequest (`routes/admin/`) après approbation.
 * La création de la demande passe par `useSubmitApprovalRequest`
 * (`@/features/shared`).
 */
export const useRecordSanctionsScreening = (partyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "parties", "sanctions-screening", partyId],
    mutationFn: ({
      status,
      reference,
      approvalRequestId,
    }: {
      status: SanctionsScreeningStatus;
      reference?: string;
      approvalRequestId?: string;
    }) => {
      const query = new URLSearchParams({ status });
      if (reference) query.set("reference", reference);
      if (approvalRequestId) query.set("approvalRequestId", approvalRequestId);
      return mutateData({
        url: `${kycEndpoints.parties.sanctionsScreening(partyId)}?${query.toString()}`,
        method: "POST",
      }) as Promise<PartyDto>;
    },
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycPartyCacheKeys(partyId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRecordSanctionsScreening failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
